import api from "./api";
import type { ModulePermissionDto } from "../utils/rolePermissions";
import type { AuthUser } from "../stores/authStore";

export interface LoginData {
  email: string;
  password: string;
  totpCode?: string;
}

export type LoginResult =
  | AuthResponse
  | { requires2fa: true };

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone: string;
  salesPointId: string;
  role?: string;
}

export interface AuthResponse {
  user: AuthUser & {
    createdAt?: string;
    updatedAt?: string;
  };
  accessToken: string;
  refreshToken: string;
}

function mapPermissionRows(raw: unknown): ModulePermissionDto[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row: Record<string, unknown>) => ({
    module: String(row.module || ""),
    canCreate: Boolean(row.canCreate),
    canRead: Boolean(row.canRead),
    canUpdate: Boolean(row.canUpdate),
    canDelete: Boolean(row.canDelete),
    extraActions: Array.isArray(row.extraActions)
      ? row.extraActions.map(String)
      : [],
  }));
}

function mapUserFromDetail(
  detail: Record<string, unknown>,
  extras?: { permissions?: unknown; featurePermissions?: unknown }
): AuthResponse["user"] {
  const topFeatures = extras?.featurePermissions;
  const effective =
    Array.isArray(topFeatures)
      ? topFeatures.map(String)
      : Array.isArray(detail.effectiveFeaturePermissions)
        ? (detail.effectiveFeaturePermissions as string[])
        : [];

  return {
    id: String(detail.id),
    name: String(detail.name),
    email: String(detail.email),
    role: String(detail.role),
    companyId: detail.companyId ? String(detail.companyId) : null,
    branchId: String(
      detail.branchId ||
        (detail.branch as { id?: string } | undefined)?.id ||
        (detail.salesPoint as { branchId?: string } | undefined)?.branchId ||
        (detail.scope as { branchId?: string } | undefined)?.branchId ||
        ""
    ) || undefined,
    salesPointId: String(
      detail.salesPointId ||
        detail.departmentId ||
        (detail.salesPoint as { id?: string } | undefined)?.id ||
        (detail.scope as { salesPointId?: string } | undefined)?.salesPointId ||
        ""
    ),
    isActive: Boolean(detail.isActive),
    phone: detail.phone ? String(detail.phone) : "",
    createdAt: detail.createdAt ? String(detail.createdAt) : undefined,
    updatedAt: detail.updatedAt ? String(detail.updatedAt) : undefined,
    featurePermissions:
      (detail.featurePermissions as Record<string, boolean>) || undefined,
    effectiveFeaturePermissions: effective,
    permissions: mapPermissionRows(extras?.permissions ?? detail.permissions),
    company: detail.company
      ? {
          id: String((detail.company as any).id),
          name: String((detail.company as any).name || ""),
          code: (detail.company as any).code
            ? String((detail.company as any).code)
            : undefined,
          gstin: (detail.company as any).gstin ?? null,
          pan: (detail.company as any).pan ?? null,
          stateCode: (detail.company as any).stateCode ?? null,
        }
      : null,
  };
}

export const authAPI = {
  login: async (data: LoginData): Promise<LoginResult> => {
    const response = await api.post("/auth/login", data);
    const apiData = response.data.data;

    if (apiData?.requires2fa) {
      return { requires2fa: true };
    }

    return {
      user: mapUserFromDetail(apiData.userDetail, {
        permissions: apiData.permissions,
        featurePermissions: apiData.featurePermissions,
      }),
      accessToken: apiData.accessToken,
      refreshToken: apiData.refreshToken || "",
    };
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post("/auth/register", data);
    return response.data.data;
  },

  requestRegisterOtp: async (email: string): Promise<{ email: string; debugOtp?: string }> => {
    const response = await api.post("/auth/register/request-otp", { email });
    return response.data.data;
  },

  registerCompany: async (data: {
    email: string;
    otp: string;
    password: string;
    name: string;
    phone?: string;
    companyName: string;
    companyCode?: string;
    gstin?: string;
    pan?: string;
    stateCode?: string;
  }) => {
    const response = await api.post("/auth/register", data);
    return response.data.data;
  },

  refreshToken: async (
    refreshToken: string
  ): Promise<{ accessToken: string }> => {
    const response = await api.post("/auth/refresh-token", { refreshToken });
    return response.data.data;
  },

  getMe: async (): Promise<AuthUser> => {
    const response = await api.get("/auth/me");
    const apiData = response.data.data;

    return mapUserFromDetail(apiData, {
      permissions: apiData.permissions,
      featurePermissions: apiData.featurePermissions,
    });
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword?: string;
  }) => {
    const response = await api.post("/auth/change-password", {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      ...(data.confirmPassword != null
        ? { confirmPassword: data.confirmPassword }
        : { confirmPassword: data.newPassword }),
    });
    return response.data;
  },

  updateUser: async (data: { name: string; email: string; phone?: string }) => {
    const response = await api.put("/auth/profile", data);
    return response.data;
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors during logout */
    }
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("paytool-auth-v2");
      localStorage.removeItem("auth-storage");
    } catch {
      /* ignore */
    }
  },

  get2faStatus: async (): Promise<{ required: boolean; enabled: boolean }> => {
    const response = await api.get("/auth/2fa/status");
    return response.data.data;
  },

  setup2fa: async (): Promise<{
    secret: string;
    otpauthUrl: string;
  }> => {
    const response = await api.post("/auth/2fa/setup");
    return response.data.data;
  },

  confirm2fa: async (totpCode: string): Promise<{ enabled: boolean }> => {
    const response = await api.post("/auth/2fa/confirm", { totpCode });
    return response.data.data;
  },

  disable2fa: async (payload: {
    password: string;
    totpCode: string;
  }): Promise<{ enabled: boolean }> => {
    const response = await api.post("/auth/2fa/disable", payload);
    return response.data.data;
  },
};
